import React, { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { Mail, Plus, Search, Users, CheckCircle2, Clock, XCircle, Tag } from "lucide-react";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/Skeleton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { cn } from "@/lib/utils";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return format(new Date(iso), "d MMM yyyy"); } catch { return iso; }
};

const StatPill = ({ status, daysLeft }) => {
    if (!status || status === "pending") {
        return (
            <span data-testid="allowlist-pill-pending" className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                <Clock className="h-3 w-3" /> Pending
            </span>
        );
    }
    if (status === "redeemed-active") {
        return (
            <span data-testid="allowlist-pill-active" className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                <CheckCircle2 className="h-3 w-3" /> Redeemed · {daysLeft}d left
            </span>
        );
    }
    return (
        <span data-testid="allowlist-pill-expired" className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
            <XCircle className="h-3 w-3" /> Redeemed · expired
        </span>
    );
};

export default function AdminAllowlistPage() {
    const [stats, setStats] = useState(null);
    const [list, setList] = useState(null);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [addOpen, setAddOpen] = useState(false);
    const [reloadTick, setReloadTick] = useState(0);
    useDocumentTitle("Admin · Allowlist");

    const reload = useCallback(() => setReloadTick((n) => n + 1), []);

    useEffect(() => {
        let cancelled = false;
        api.get("/admin/allowlist/stats").then((r) => {
            if (!cancelled) setStats(r.data);
        });
        return () => { cancelled = true; };
    }, [reloadTick]);

    useEffect(() => {
        let cancelled = false;
        setList(null);
        const params = { page_size: 50 };
        if (search.trim()) params.search = search.trim();
        if (statusFilter !== "all") params.status = statusFilter;
        api.get("/admin/allowlist", { params }).then((r) => {
            if (!cancelled) setList(r.data);
        });
        return () => { cancelled = true; };
    }, [search, statusFilter, reloadTick]);

    const handleDelete = async (entryId, email) => {
        if (!window.confirm(`Remove ${email} from the allowlist?`)) return;
        try {
            await api.delete(`/admin/allowlist/${entryId}`);
            toast.success(`Removed ${email}`);
            reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to remove");
        }
    };

    return (
        <Layout>
            <div className="mx-auto max-w-6xl space-y-6 px-4 py-8" data-testid="admin-allowlist-page">
                <header className="space-y-2">
                    <h1 className="text-3xl font-semibold tracking-tight text-foreground">
                        Complimentary Access · Email Allowlist
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Grant pre-paid users 3 months of free Happy Lab access by adding their email.
                        When they register or log in, the complimentary subscription is applied automatically.
                    </p>
                </header>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <StatCard label="Total entries" value={stats?.total_entries} icon={Mail} />
                    <StatCard label="Redeemed" value={stats?.redeemed} icon={CheckCircle2} />
                    <StatCard label="Pending" value={stats?.pending} icon={Clock} />
                    <StatCard label="Active grants" value={stats?.redeemed_active} icon={Users} />
                </div>

                {/* Filter bar + Add button */}
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            data-testid="allowlist-search"
                            placeholder="Search email or cohort label…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9"
                        />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-[160px]" data-testid="allowlist-status-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="redeemed">Redeemed</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button
                        data-testid="allowlist-add-button"
                        onClick={() => setAddOpen(true)}
                        className="ml-auto"
                    >
                        <Plus className="mr-1.5 h-4 w-4" /> Add emails
                    </Button>
                </div>

                {/* Cohort summary */}
                {stats?.by_cohort?.length > 0 && (
                    <div className="flex flex-wrap gap-2" data-testid="allowlist-cohorts">
                        {stats.by_cohort.map((c) => (
                            <span
                                key={c.label}
                                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-medium text-foreground/80"
                            >
                                <Tag className="h-3 w-3" /> {c.label}
                                <span className="text-muted-foreground">· {c.redeemed}/{c.total}</span>
                            </span>
                        ))}
                    </div>
                )}

                {/* Table */}
                <Card className="overflow-hidden">
                    <div className="hidden md:block">
                        <table className="w-full text-sm">
                            <thead className="border-b border-border bg-secondary/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                                <tr>
                                    <th className="px-4 py-2.5">Email</th>
                                    <th className="px-4 py-2.5">Cohort</th>
                                    <th className="px-4 py-2.5">Status</th>
                                    <th className="px-4 py-2.5">Added</th>
                                    <th className="px-4 py-2.5">Redeemed</th>
                                    <th className="px-4 py-2.5">Expires</th>
                                    <th className="px-4 py-2.5 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody data-testid="allowlist-table-body">
                                {!list && Array.from({ length: 5 }).map((_, i) => (
                                    <tr key={i} className="border-b border-border/50">
                                        <td className="px-4 py-3" colSpan={7}>
                                            <Skeleton className="h-5 w-full" />
                                        </td>
                                    </tr>
                                ))}
                                {list?.items?.length === 0 && (
                                    <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                                        No emails on allowlist yet.
                                    </td></tr>
                                )}
                                {list?.items?.map((e) => {
                                    const status = !e.redeemed_at
                                        ? "pending"
                                        : (e.days_until_expiry > 0 ? "redeemed-active" : "redeemed-expired");
                                    return (
                                        <tr key={e.id} className="border-b border-border/50 last:border-b-0" data-testid={`allowlist-row-${e.email}`}>
                                            <td className="px-4 py-3 font-medium text-foreground">{e.email}</td>
                                            <td className="px-4 py-3 text-muted-foreground">{e.grant_label || "—"}</td>
                                            <td className="px-4 py-3">
                                                <StatPill status={status} daysLeft={e.days_until_expiry} />
                                            </td>
                                            <td className="px-4 py-3 text-muted-foreground">{fmtDate(e.added_at)}</td>
                                            <td className="px-4 py-3 text-muted-foreground">{fmtDate(e.redeemed_at)}</td>
                                            <td className="px-4 py-3 text-muted-foreground">{fmtDate(e.redeemed_subscription_expires_at)}</td>
                                            <td className="px-4 py-3 text-right">
                                                <Button
                                                    data-testid={`allowlist-delete-${e.email}`}
                                                    variant="ghost"
                                                    size="sm"
                                                    disabled={!!e.redeemed_at}
                                                    onClick={() => handleDelete(e.id, e.email)}
                                                    className={cn("text-rose-600 hover:bg-rose-50 hover:text-rose-700", e.redeemed_at && "opacity-40")}
                                                >
                                                    Delete
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {/* Mobile card list */}
                    <div className="md:hidden">
                        {list?.items?.map((e) => {
                            const status = !e.redeemed_at
                                ? "pending"
                                : (e.days_until_expiry > 0 ? "redeemed-active" : "redeemed-expired");
                            return (
                                <div key={e.id} className="border-b border-border/50 p-4 last:border-b-0">
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <p className="font-medium text-foreground">{e.email}</p>
                                            <p className="text-xs text-muted-foreground">{e.grant_label || "—"}</p>
                                        </div>
                                        <StatPill status={status} daysLeft={e.days_until_expiry} />
                                    </div>
                                    <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
                                        <span>Added {fmtDate(e.added_at)}</span>
                                        {e.redeemed_at && <span>Expires {fmtDate(e.redeemed_subscription_expires_at)}</span>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Card>

                <AddEmailsDialog
                    open={addOpen}
                    onClose={() => setAddOpen(false)}
                    onAdded={() => { setAddOpen(false); reload(); }}
                />
            </div>
        </Layout>
    );
}

const StatCard = ({ label, value, icon: Icon }) => (
    <Card data-testid={`allowlist-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        <CardContent className="space-y-1 p-4">
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground">
                <Icon className="h-3 w-3" /> {label}
            </div>
            <p className="text-2xl font-semibold text-foreground">
                {value === undefined ? <Skeleton className="h-7 w-12" /> : value}
            </p>
        </CardContent>
    </Card>
);

const AddEmailsDialog = ({ open, onClose, onAdded }) => {
    const [text, setText] = useState("");
    const [label, setLabel] = useState("");
    const [notes, setNotes] = useState("");
    const [days, setDays] = useState(90);
    const [submitting, setSubmitting] = useState(false);

    const parseEmails = (raw) => raw
        .split(/[\n,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);

    const handleSubmit = async () => {
        const emails = parseEmails(text);
        if (emails.length === 0) {
            toast.error("Paste at least one email");
            return;
        }
        setSubmitting(true);
        try {
            const r = await api.post("/admin/allowlist", {
                emails,
                grant_duration_days: Number(days) || 90,
                grant_label: label.trim() || null,
                notes: notes.trim() || null,
            });
            const d = r.data;
            toast.success(
                `Added ${d.added.length} · Skipped ${d.skipped_duplicates.length} duplicates · Invalid ${d.invalid.length}`
            );
            setText(""); setLabel(""); setNotes(""); setDays(90);
            onAdded?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to add");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent data-testid="allowlist-add-dialog" className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Add emails to allowlist</DialogTitle>
                    <DialogDescription>
                        Each email will get an auto-applied complimentary subscription on next register or login.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <label className="mb-1 block text-sm font-medium text-foreground">Emails</label>
                        <Textarea
                            data-testid="allowlist-emails-input"
                            placeholder="Paste one email per line (or comma/semicolon-separated)"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            rows={6}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="mb-1 block text-sm font-medium text-foreground">Cohort label</label>
                            <Input
                                data-testid="allowlist-label-input"
                                placeholder="e.g. Internship Cohort 2026"
                                value={label}
                                onChange={(e) => setLabel(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-foreground">Duration (days)</label>
                            <Input
                                data-testid="allowlist-days-input"
                                type="number"
                                min={1}
                                max={3650}
                                value={days}
                                onChange={(e) => setDays(e.target.value)}
                            />
                            <p className="mt-1 text-xs text-muted-foreground">
                                Standard internship grant is 90 days = 3 months.
                            </p>
                        </div>
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-foreground">Notes (optional)</label>
                        <Input
                            data-testid="allowlist-notes-input"
                            placeholder="Internal reference"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
                    <Button
                        data-testid="allowlist-submit-button"
                        onClick={handleSubmit}
                        disabled={submitting || !text.trim()}
                    >
                        {submitting ? "Adding…" : "Add to allowlist"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
